import React from 'react';
import {
  Scale,
  Ruler,
  Info,
  TrendingUp,
  Target,
  Activity,
  ChevronRight,
  Sparkles,
  ArrowRight,
  Heart,
  Zap
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

const MedidasCard = ({ medidas }) => {
  if (!medidas) return null;

  const actualWeight = (medidas.peso.actual.min + medidas.peso.actual.max) / 2;
  const idealWeight = medidas.peso.ideal;
  const middleWeight = idealWeight / 2;
  const pesoProgress = (actualWeight / idealWeight) * 100;

  // Determine status color based on weight range
  const getStatusColor = () => {
    if (medidas.peso.actual.max < idealWeight * 0.9) return 'yellow';
    if (medidas.peso.actual.min > idealWeight * 1.1) return 'red';
    return 'green';
  };

  const statusColor = getStatusColor();
  
  // Color configurations based on status
  const colorConfig = {
    green: {
      gradient: 'from-green-500/10 to-transparent',
      iconBg: 'from-green-500 to-green-600',
      progressBg: 'bg-green-100',
      progressIndicator: 'bg-green-500',
      badge: 'bg-green-100 text-green-700 border border-green-200',
      actionCardBg: 'from-green-50 to-white',
      actionCardBorder: 'border-green-100',
      actionTextColor: 'text-green-700',
      actionIconColor: 'text-green-500',
      patternColor: 'text-green-100'
    },
    yellow: {
      gradient: 'from-amber-500/10 to-transparent',
      iconBg: 'from-amber-500 to-amber-600',
      progressBg: 'bg-amber-100',
      progressIndicator: 'bg-amber-500',
      badge: 'bg-amber-100 text-amber-700 border border-amber-200',
      actionCardBg: 'from-amber-50 to-white',
      actionCardBorder: 'border-amber-100',
      actionTextColor: 'text-amber-700',
      actionIconColor: 'text-amber-500',
      patternColor: 'text-amber-100'
    },
    red: {
      gradient: 'from-red-500/10 to-transparent',
      iconBg: 'from-red-500 to-red-600',
      progressBg: 'bg-red-100',
      progressIndicator: 'bg-red-500',
      badge: 'bg-red-100 text-red-700 border border-red-200',
      actionCardBg: 'from-red-50 to-white',
      actionCardBorder: 'border-red-100',
      actionTextColor: 'text-red-700',
      actionIconColor: 'text-red-500',
      patternColor: 'text-red-100'
    }
  };

  const colors = colorConfig[statusColor];

  return (
    <Card className="overflow-hidden bg-white/90 shadow-md border-0 relative">
      {/* Decorative Pattern */}
      <div className="absolute top-0 right-0 w-32 h-32 overflow-hidden opacity-10">
        <div className="rotate-45 transform origin-top-right">
          {Array(5).fill(0).map((_, i) => (
            <div key={i} className={`w-16 h-16 ${colors.patternColor}`}>
              <Sparkles className="w-full h-full" />
            </div>
          ))}
        </div>
      </div>
      
      {/* Encabezado del Card */}
      <CardHeader className="border-b bg-gradient-to-r from-blue-500/10 to-transparent p-5">
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
            <Scale className="h-6 w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-gray-800">
              Medidas Físicas
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Seguimiento del crecimiento y desarrollo
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Contenido principal */}
      <CardContent className="p-5 space-y-6">
        {/* Panel de Peso Principal */}
        <div className="relative bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-5">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-base text-gray-500 font-medium">Peso Actual</p>
                <div className="flex items-center gap-1.5 bg-blue-50 px-2.5 py-1 rounded-full">
                  <Info className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-blue-600 font-medium">Estimación basada en fotos</span>
                </div>
              </div>
              <div className="flex items-baseline gap-1.5">
                <h3 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                  {medidas.peso.actual.min}-{medidas.peso.actual.max}
                </h3>
                <span className="text-xl sm:text-2xl text-gray-500">
                  {medidas.peso.actual.unidad}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-500" />
                <p className="text-base text-gray-700">
                  Peso ideal: <span className="font-semibold">{medidas.peso.ideal} kg</span>
                </p>
              </div>
            </div>

            <div className="flex flex-col items-start sm:items-end gap-2">
              <span
                className={cn(
                  'inline-flex items-center px-4 py-2 rounded-full text-sm font-medium shadow-sm',
                  colors.badge
                )}
              >
                {pesoProgress < 90
                  ? 'Por debajo del ideal'
                  : pesoProgress > 110
                    ? 'Por encima del ideal'
                    : 'Peso ideal'}
              </span>
            </div>
          </div>

          {/* Barra de progreso */}
          <div className="mt-6">
            <div className="flex justify-between text-base text-gray-500 mb-3">
              <span className="font-medium">0 kg</span>
              <span className="text-blue-600 font-semibold">
                {middleWeight} kg
              </span>
              <span className="font-medium">{idealWeight} kg</span>
            </div>
            <div className="relative">
              <Progress
                value={pesoProgress}
                className={`h-4 ${colors.progressBg} rounded-full`}
                indicatorClassName={cn(
                  'rounded-full transition-all',
                  colors.progressIndicator
                )}
              />
              <div className="absolute top-0 left-1/2 h-5 w-0.5 -mt-0.5 bg-blue-400" />
              
              {/* Animated marker for current weight */}
              <div 
                className="absolute top-0 -mt-1 transform -translate-x-1/2 transition-all duration-500"
                style={{ left: `${Math.min(Math.max(pesoProgress, 0), 100)}%` }}
              >
                <div className="h-6 w-6 rounded-full bg-white border-2 border-blue-500 shadow-md flex items-center justify-center animate-pulse">
                  <Zap className="h-3 w-3 text-blue-500" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tarjetas Informativas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Rango Saludable */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 hover:border-blue-200 transition-all duration-300 overflow-hidden shadow-sm hover:shadow-md transform hover:scale-[1.01]">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Target className="h-5 w-5 text-blue-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  Rango Saludable
                </h4>
              </div>
              <p className="text-base text-gray-700 leading-relaxed">
                {medidas.peso.contexto.rangoPorRaza}
              </p>
            </div>
          </div>

          {/* Plan de Acción */}
          <div className={`bg-gradient-to-br ${colors.actionCardBg} rounded-xl ${colors.actionCardBorder} overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 transform hover:scale-[1.01]`}>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-${statusColor}-100 rounded-lg`}>
                  <TrendingUp className={`h-5 w-5 ${colors.actionIconColor}`} />
                </div>
                <h4 className="text-lg font-semibold text-gray-900">
                  Plan de Acción
                </h4>
              </div>
              <div className="space-y-2.5">
                {Object.entries(medidas.peso.planAccion).map(([key, value], idx) => (
                  <div
                    key={idx}
                    className="group flex items-start gap-3 bg-white/70 p-4 rounded-lg hover:bg-white transition-colors border border-transparent hover:border-blue-100 cursor-pointer shadow-sm hover:shadow-md"
                  >
                    <div className="mt-0.5 bg-white h-6 w-6 rounded-full shadow-sm border border-gray-200 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <ArrowRight className={`h-3.5 w-3.5 ${colors.actionIconColor} group-hover:translate-x-0.5 transition-transform`} />
                    </div>
                    <div>
                      <p className={`text-base font-medium ${colors.actionTextColor} capitalize`}>
                        {key}
                      </p>
                      <p className="text-base text-gray-600 mt-1">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sección Talla (opcional) */}
        {medidas.talla && (
          <div className="pt-6 border-t border-gray-100 space-y-5">
            <div className="flex items-center gap-4">
              <div className="p-2.5 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-md">
                <Ruler className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800">Altura</h3>
                <p className="text-base text-gray-500">Medición actual</p>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-5 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-baseline gap-2">
                <p className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
                  {medidas.talla.actual}
                </p>
                <span className="text-xl sm:text-2xl text-gray-500">cm</span>
              </div>

              {medidas.talla.contexto && (
                <div className="space-y-3">
                  {Object.values(medidas.talla.contexto).map((value, idx) => (
                    value && (
                      <div
                        key={idx}
                        className="flex items-start gap-3 bg-green-50/70 p-4 rounded-lg hover:bg-green-50 transition-colors border border-green-100 shadow-sm"
                      >
                        <Info className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                        <p className="text-base text-gray-700">{value}</p>
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MedidasCard;